import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import { Package, PackageCheck, HelpCircle, FileSearch01 } from '@untitledui/icons';
import { getLocalTimeZone, today, startOfMonth, endOfMonth, parseDate } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
import { useTranslation } from 'react-i18next';

type RangeValue<T> = { start: T; end: T } | null;
import { useContainerStatsRecap } from '@/hooks/api/use-containers-recap';
import { useReviewItems } from '@/hooks/api/use-review-center';
import { useCompanyProfile } from '@/hooks/api/use-company-profile';
import { useCarriers } from '@/hooks/api/use-carriers';
import { useEmailAccounts } from '@/hooks/api/use-email-accounts';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import { DateRangePicker } from '@/components/application/date-picker/date-range-picker';
import { Select } from '@/components/base/select/select';
import { Button } from '@/components/base/buttons/button';
import { RiskyShipmentsTable } from '@/components/application/containers/risky-shipments-table';
import { AttentionDetail } from '@/components/application/control-hub/attention-detail';
import type { ContainerStats } from '@/types/container';
import type { ShipmentStatusFilter } from '@/types/api/containers-recap';
import { SHIPMENT_STATUS_OPTIONS } from '@/types/api/containers-recap';
import { ShipperControlHubOnboardingTodos } from '@/components/application/shipper/shipper-control-hub-onboarding-todos';
import { ShipperControlHubPerformanceClusters } from '@/components/application/shipper/shipper-control-hub-performance-clusters';
import { ShipperControlHubCompanySnapshot } from '@/components/application/shipper/shipper-control-hub-company-snapshot';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';

const PROFILE_COMPLETE_MIN = 80;

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color: 'brand' | 'success' | 'warning' | 'error';
    tooltip?: {
        title: string;
        description?: string;
    };
    className?: string;
    onClick?: () => void;
    role?: string;
    tabIndex?: number;
    onKeyDown?: (e: React.KeyboardEvent) => void;
}

const StatCard = ({ title, value, icon: Icon, color, tooltip, className, onClick, role, tabIndex, onKeyDown }: StatCardProps) => {
    const colorClasses = {
        brand: 'text-brand-600 bg-brand-50',
        success: 'text-success-600 bg-success-50',
        warning: 'text-warning-600 bg-warning-50',
        error: 'text-error-600 bg-error-50',
    };

    return (
        <div
            className={`rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary ${className ?? ''}`}
            onClick={onClick}
            role={role}
            tabIndex={tabIndex}
            onKeyDown={onKeyDown}
        >
            <div className="flex items-center">
                <div className={`rounded-md p-2 ${colorClasses[color]}`}>
                    <Icon className="size-5" />
                </div>
                <div className="ml-3 flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                        <p className="text-xs font-medium text-tertiary truncate">{title}</p>
                        {tooltip && (
                            <Tooltip title={tooltip.title} description={tooltip.description} placement="top" arrow>
                                <TooltipTrigger>
                                    <HelpCircle className="size-3 shrink-0 text-quaternary hover:text-tertiary cursor-help" />
                                </TooltipTrigger>
                            </Tooltip>
                        )}
                    </div>
                    <p className="text-xl font-semibold text-primary tabular-nums">{value}</p>
                </div>
            </div>
        </div>
    );
};

interface ClickableStatCardProps extends StatCardProps {
    href?: string;
    className?: string;
}

const ClickableStatCard = ({ href, className: wrapperClassName, ...props }: ClickableStatCardProps) => {
    const location = useLocation();
    const navigate = useNavigate();

    if (!href) {
        return <StatCard {...props} className={wrapperClassName} />;
    }

    return (
        <StatCard
            {...props}
            className={`cursor-pointer transition-all hover:ring-2 hover:ring-brand-200 ${wrapperClassName ?? ''}`}
            onClick={() => navigate(href, { state: navigationStateFrom(location) })}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(href, { state: navigationStateFrom(location) })}
        />
    );
};

const getDateRangeStrings = (range: RangeValue<DateValue> | null): { startDate: string | null; endDate: string | null } => {
    if (!range || !range.start || !range.end) {
        return { startDate: null, endDate: null };
    }
    const formatDate = (d: DateValue) => {
        return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
    };
    return {
        startDate: formatDate(range.start),
        endDate: formatDate(range.end),
    };
};

export const ShipperControlHubPage = () => {
    const { t, i18n } = useTranslation(['pages:dashboard', 'pages:containers', 'common']);
    const [searchParams, setSearchParams] = useSearchParams();

    const now = today(getLocalTimeZone());
    const defaultStart = startOfMonth(now);
    const defaultEnd = endOfMonth(now);
    const defaultStartStr = `${defaultStart.year}-${String(defaultStart.month).padStart(2, '0')}-${String(defaultStart.day).padStart(2, '0')}`;
    const defaultEndStr = `${defaultEnd.year}-${String(defaultEnd.month).padStart(2, '0')}-${String(defaultEnd.day).padStart(2, '0')}`;

    const getInitialDateRange = (): RangeValue<DateValue> => {
        const startParam = searchParams.get('start_date');
        const endParam = searchParams.get('end_date');
        if (startParam && endParam) {
            try {
                return { start: parseDate(startParam), end: parseDate(endParam) };
            } catch {
                /* fall through */
            }
        }
        return { start: defaultStart, end: defaultEnd };
    };

    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>(getInitialDateRange);
    const [carrierFilter, setCarrierFilter] = useState<string>(searchParams.get('carrier') || '');
    const [statusFilter, setStatusFilter] = useState<ShipmentStatusFilter | ''>(
        (searchParams.get('status') as ShipmentStatusFilter) || ''
    );
    const isInitializing = useRef(true);
    useEffect(() => {
        const timer = setTimeout(() => {
            isInitializing.current = false;
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    const handleDateRangeChange = (value: RangeValue<DateValue> | null) => {
        setDateRange(value);
    };

    const { startDate, endDate } = useMemo(() => getDateRangeStrings(dateRange), [dateRange]);

    const quickStatsTitle = useMemo(() => {
        if (!startDate || !endDate) {
            return t('controlHub.quickStats.titleNoRange');
        }
        const fmt = new Intl.DateTimeFormat(i18n.language, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
        const start = fmt.format(new Date(`${startDate}T12:00:00`));
        const end = fmt.format(new Date(`${endDate}T12:00:00`));
        return t('controlHub.quickStats.title', { start, end });
    }, [startDate, endDate, i18n.language, t]);

    const { data: carriersData } = useCarriers({ carrier_type: 'ocean' }, true);
    const carrierOptions = useMemo(() => {
        const options = [{ id: '', label: t('controlHub.toolbar.allCarriers') }];
        if (carriersData?.carriers) {
            carriersData.carriers.forEach((carrier) => {
                options.push({ id: carrier.scac, label: carrier.name });
            });
        }
        return options;
    }, [carriersData, t]);

    const statusOptions = useMemo(
        () =>
            SHIPMENT_STATUS_OPTIONS.filter((opt) => opt.id !== '').map((opt) => ({
                id: opt.id,
                label: opt.label,
            })),
        []
    );

    const hasActiveFilters = carrierFilter !== '' || statusFilter !== '';
    const dateRangeIsDefault = startDate === defaultStartStr && endDate === defaultEndStr;
    const pageFiltersActiveForSnapshot = hasActiveFilters || !dateRangeIsDefault;

    const clearFilters = () => {
        setCarrierFilter('');
        setStatusFilter('');
    };

    const syncFiltersToUrl = useCallback(() => {
        if (isInitializing.current) return;

        const params = new URLSearchParams();

        if (startDate && startDate !== defaultStartStr) params.set('start_date', startDate);
        if (endDate && endDate !== defaultEndStr) params.set('end_date', endDate);
        if (carrierFilter) params.set('carrier', carrierFilter);
        if (statusFilter) params.set('status', statusFilter);
        setSearchParams(params, { replace: true });
    }, [startDate, endDate, carrierFilter, statusFilter, defaultStartStr, defaultEndStr, setSearchParams]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            syncFiltersToUrl();
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [syncFiltersToUrl]);

    const {
        data: statsData,
        isLoading: statsLoading,
    } = useContainerStatsRecap({
        startDate,
        endDate,
        carrierScac: carrierFilter || undefined,
        shipmentStatus: statusFilter ? [statusFilter] : undefined,
        enabled: true,
    });

    const {
        data: companyProfile,
        isLoading: companyProfileLoading,
        error: companyProfileError,
    } = useCompanyProfile(true);

    const { data: emailAccounts } = useEmailAccounts();

    const { data: pendingReviewData } = useReviewItems({ status: 'pending', limit: 1 });

    const stats: ContainerStats = useMemo(() => {
        if (statsData) {
            return {
                total: statsData.total_containers,
                inTransit: statsData.in_transit,
                delivered: statsData.delivered,
                delayed: 0,
                docIssues: statsData.containers_with_doc_issues ?? statsData.doc_issue_count ?? statsData.pending_documents,
                activeAlerts: statsData.total_issue_count ?? statsData.containers_with_issues,
                highRisk: statsData.high_risk,
            };
        }
        return {
            total: 0,
            inTransit: 0,
            delivered: 0,
            delayed: 0,
            docIssues: 0,
            activeAlerts: 0,
            highRisk: 0,
        };
    }, [statsData]);

    const pendingReviewCount = pendingReviewData?.total ?? 0;

    const buildContainersUrl = (params: Record<string, string>) => {
        const sp = new URLSearchParams();
        if (startDate) sp.set('start_date', startDate);
        if (endDate) sp.set('end_date', endDate);
        Object.entries(params).forEach(([key, value]) => {
            sp.set(key, value);
        });
        return `/shipper/containers?${sp.toString()}`;
    };

    const autoEmailConnected = (emailAccounts?.length ?? 0) > 0;
    const autoProfileComplete =
        (companyProfile?.profile_completeness_score ?? 0) >= PROFILE_COMPLETE_MIN ||
        companyProfile?.shipper_operational_profile_complete === true;
    const autoHasContainer = (statsData?.total_containers ?? 0) > 0;

    if (statsLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-lg text-tertiary">{t('shipper.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="space-y-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-brand-secondary">{t('controlHub.eyebrow')}</p>
                        <h1 className="mt-1 text-2xl font-semibold text-primary">{t('controlHub.title', { ns: 'common' })}</h1>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <ShipperOnboardingHeaderAction />
                        <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
                        <div className="w-full min-w-[11rem] sm:w-44">
                            <Select
                                placeholder={t('controlHub.toolbar.carrierPlaceholder')}
                                items={carrierOptions}
                                selectedKey={carrierFilter}
                                onSelectionChange={(key) => setCarrierFilter(key as string)}
                                size="sm"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                        <div className="w-full min-w-[11rem] sm:w-44">
                            <Select
                                placeholder={t('controlHub.toolbar.statusPlaceholder')}
                                items={[{ id: '', label: t('controlHub.toolbar.allStatuses') }, ...statusOptions]}
                                selectedKey={statusFilter}
                                onSelectionChange={(key) => setStatusFilter(key as ShipmentStatusFilter | '')}
                                size="sm"
                            >
                                {(item) => (
                                    <Select.Item key={item.id} id={item.id}>
                                        {item.label}
                                    </Select.Item>
                                )}
                            </Select>
                        </div>
                        {hasActiveFilters && (
                            <Button color="tertiary" size="sm" onClick={clearFilters}>
                                {t('controlHub.toolbar.clear')}
                            </Button>
                        )}
                    </div>
                </div>
                <p className="w-full text-tertiary">{t('controlHub.pageSubtitle')}</p>
            </header>

            <ShipperControlHubOnboardingTodos
                variant="banner"
                autoEmailConnected={autoEmailConnected}
                autoProfileComplete={autoProfileComplete}
                autoHasContainer={autoHasContainer}
            />

            <ShipperControlHubPerformanceClusters
                stats={statsData}
                buildContainersUrl={buildContainersUrl}
                issuesHref="/shipper/issues"
                reviewHref="/shipper/review-center"
                pendingReviewCount={pendingReviewCount}
            />

            <section aria-labelledby="quick-stats-heading" className="space-y-2">
                <div>
                    <h2 id="quick-stats-heading" className="text-base font-semibold text-primary">
                        {quickStatsTitle}
                    </h2>
                    <p className="text-sm text-tertiary">{t('controlHub.quickStats.subtitle')}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <ClickableStatCard
                        title={t('shipper.stats.totalContainers')}
                        value={stats.total}
                        icon={Package}
                        color="brand"
                        href={buildContainersUrl({})}
                    />
                    <ClickableStatCard
                        title={t('shipper.stats.inTransit')}
                        value={stats.inTransit}
                        icon={PackageCheck}
                        color="success"
                        href={buildContainersUrl({ status: 'in_transit' })}
                    />
                    <ClickableStatCard
                        title={t('shipper.stats.delivered')}
                        value={stats.delivered}
                        icon={PackageCheck}
                        color="success"
                        href={buildContainersUrl({ status: 'delivered' })}
                    />
                    <ClickableStatCard
                        title={t('controlHub.stats.pendingReviews')}
                        value={pendingReviewCount}
                        icon={FileSearch01}
                        color="brand"
                        tooltip={{
                            title: t('controlHub.tooltips.pendingReviews.title'),
                            description: t('controlHub.tooltips.pendingReviews.description'),
                        }}
                        href="/shipper/review-center"
                    />
                </div>
            </section>

            <RiskyShipmentsTable
                startDate={startDate}
                endDate={endDate}
                carrierScac={carrierFilter || undefined}
                shipmentStatus={statusFilter || undefined}
                includeMedium={false}
                title={t('controlHub.ledger.title')}
                description={t('controlHub.ledger.description')}
            />

            <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
                <ShipperControlHubCompanySnapshot
                    profile={companyProfile}
                    isLoading={companyProfileLoading}
                    error={companyProfileError}
                    recapTotalContainers={statsData?.total_containers}
                    pageFiltersActive={pageFiltersActiveForSnapshot}
                />
                <AttentionDetail
                    startDate={startDate}
                    endDate={endDate}
                    carrierScac={carrierFilter || undefined}
                    shipmentStatus={statusFilter || undefined}
                />
            </div>
        </div>
    );
};
